import React, { useState } from 'react';
import axios from '../api/axios';

export default function ReportsPresences() {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [records, setRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const formatTime = (value) => {
    if (!value) {
      return '--';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '--';
    }
    return date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  };

  const handleSearch = async (event) => {
    event.preventDefault();
    setError(null);

    if (!from || !to) {
      setError('Selecciona un rango de fechas.');
      return;
    }
    if (from > to) {
      setError('La fecha inicial no puede ser mayor que la final.');
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.get('/attendance/report', { params: { from, to } });
      setRecords(response.data);
    } catch (err) {
      const message = err.response?.data?.message || 'No se pudieron cargar los registros.';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!from || !to) {
      setError('Selecciona un rango de fechas antes de descargar.');
      return;
    }
    try {
      const response = await axios.get('/attendance/report/pdf', {
        params: { from, to },
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `presencias_${from}_${to}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      const message = err.response?.data?.message || 'No se pudo descargar el PDF.';
      setError(message);
    }
  };

  return (
    <div className="container mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800">Informe de presencias</h1>
        <p className="text-sm text-gray-500">Selecciona un rango de fechas para ver los registros.</p>
      </div>

      <form onSubmit={handleSearch} className="mb-6 grid gap-4 md:grid-cols-3">
        <div>
          <label className="text-sm font-semibold text-gray-600">Desde</label>
          <input
            type="date"
            value={from}
            onChange={(event) => setFrom(event.target.value)}
            className="mt-1 w-full rounded-lg border border-gray-200 p-2"
          />
        </div>
        <div>
          <label className="text-sm font-semibold text-gray-600">Hasta</label>
          <input
            type="date"
            value={to}
            onChange={(event) => setTo(event.target.value)}
            className="mt-1 w-full rounded-lg border border-gray-200 p-2"
          />
        </div>
        <div className="flex items-end gap-2">
          <button
            type="submit"
            disabled={isLoading}
            className="w-full rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-60"
          >
            {isLoading ? 'Buscando...' : 'Buscar'}
          </button>
          <button
            type="button"
            onClick={handleDownloadPdf}
            className="w-full rounded-lg border border-gray-200 px-4 py-2 text-gray-600 hover:bg-gray-50"
          >
            Descargar PDF
          </button>
        </div>
      </form>

      {error && <div className="mb-4 rounded-lg bg-red-50 px-4 py-2 text-sm text-red-600">{error}</div>}

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        {isLoading ? (
          <div className="p-6 text-sm text-gray-500">Cargando registros...</div>
        ) : records.length === 0 ? (
          <div className="p-6 text-sm text-gray-500">No hay registros para el rango seleccionado.</div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Fecha
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Empleado
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Entrada
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Salida
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Minutos
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {records.map((record) => (
                <tr key={record.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm text-gray-700">{record.date}</td>
                  <td className="px-6 py-4 text-sm text-gray-700">
                    {record.employeeName || `Empleado ${record.employeeId}`}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">{formatTime(record.checkInAt)}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">{formatTime(record.checkOutAt)}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">{record.status}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">{record.workedMinutes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
